import { useEffect, useMemo, useState } from "react";
import { BotOff, LockKeyhole, Pencil, Plus, Save, Tags } from "lucide-react";
import { useSearchParams } from "react-router-dom";

import { Badge, Button, Card } from "@/design-system/primitives";
import {
  type ClinicianInboxTagInput,
  useClinicianRulesAutomations,
  useClinicianSession,
  useCreateClinicianInboxTag,
  useUpdateClinicianAutoSilence,
  useUpdateClinicianInboxTag,
} from "@/lib/api/clinicianHooks";
import type {
  ClinicianInboxTagPriority,
  ClinicianInboxTagRule,
} from "@/types/clinician";
import {
  type SilenceDurationUnit,
  silenceDurationFromMinutes,
  silenceDurationToMinutes,
  validSilenceDuration,
} from "./rulesAutomationForm";
import { MessageAutomations } from "./MessageAutomations";

type RulesTab = "rules" | "tags" | "automations";

const TABS: { value: RulesTab; label: string }[] = [
  { value: "rules", label: "Inbox Rules" },
  { value: "tags", label: "Inbox Tags" },
  { value: "automations", label: "Inbox Automations" },
];

const fieldClass =
  "min-h-38 border border-line bg-raised px-9 type-body-sm text-ink outline-none focus:shadow-focus disabled:opacity-40";

export function RulesAutomationsScreen() {
  const [params, setParams] = useSearchParams();
  const requestedTab = params.get("tab") as RulesTab | null;
  const tab = TABS.some((item) => item.value === requestedTab)
    ? requestedTab!
    : "rules";

  return (
    <main className="mx-auto max-w-[100rem] px-14 py-24 md:px-30 md:py-40 xl:px-40">
      <header className="flex flex-col gap-5">
        <span className="type-label-sm text-ink-muted">CLINICAL SETTINGS</span>
        <h1 className="type-heading-xl">Rules &amp; automations</h1>
        <p className="max-w-[68rem] type-body-sm text-ink-muted">
          Control how the clinical inbox behaves and manage patient messaging
          workflows. Safety and clinical escalation guardrails always remain in
          force.
        </p>
      </header>

      <div className="mt-20 flex border-b border-line-subtle" role="tablist">
        {TABS.map((item) => (
          <button
            key={item.value}
            type="button"
            role="tab"
            aria-selected={tab === item.value}
            onClick={() => {
              const next = new URLSearchParams(params);
              if (item.value === "rules") next.delete("tab");
              else next.set("tab", item.value);
              setParams(next, { replace: true });
            }}
            className={`-mb-px border-b-2 px-12 py-9 type-label-sm ${
              tab === item.value
                ? "border-ink text-ink"
                : "border-transparent text-ink-muted hover:text-ink"
            }`}
          >
            {item.label}
          </button>
        ))}
      </div>

      <div className="mt-20">
        {tab === "rules" && <InboxRules />}
        {tab === "tags" && <InboxTags />}
        {tab === "automations" && <InboxAutomations />}
      </div>
    </main>
  );
}

function InboxRules() {
  const rules = useClinicianRulesAutomations();
  const update = useUpdateClinicianAutoSilence();
  const [enabled, setEnabled] = useState(true);
  const [amount, setAmount] = useState(1);
  const [unit, setUnit] = useState<SilenceDurationUnit>("hours");
  const [dirty, setDirty] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);

  useEffect(() => {
    if (!rules.data || dirty) return;
    const duration = silenceDurationFromMinutes(
      rules.data.auto_silence.duration_minutes,
    );
    setEnabled(rules.data.auto_silence.enabled);
    setAmount(duration.amount);
    setUnit(duration.unit);
  }, [dirty, rules.data]);

  const durationMinutes = silenceDurationToMinutes(amount, unit);
  const valid = validSilenceDuration(durationMinutes);

  const save = async () => {
    if (!valid) return;
    setNotice(null);
    try {
      await update.mutateAsync({
        enabled,
        duration_minutes: durationMinutes,
      });
      setDirty(false);
      setNotice("Inbox rule saved.");
    } catch {
      setNotice("The inbox rule could not be saved. Nothing was changed.");
    }
  };

  return (
    <section className="flex max-w-[72rem] flex-col gap-12">
      <div>
        <h2 className="type-heading-md">AI auto-responder</h2>
        <p className="mt-4 type-body-sm text-ink-muted">
          Decide how long automatic patient replies pause after a clinician
          responds.
        </p>
      </div>

      {rules.isLoading ? (
        <Card className="h-[12rem] animate-pulse bg-sunken" />
      ) : rules.isError ? (
        <Card
          className="border-danger/30 bg-danger-wash text-danger"
          role="alert"
        >
          Inbox rules could not be loaded. Nothing was changed.
        </Card>
      ) : (
        <Card className="flex flex-col gap-14" padding="lg">
          <div className="flex flex-wrap items-start justify-between gap-14">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-7">
                <BotOff size={16} aria-hidden />
                <h3 className="type-heading-sm">
                  Automatically silence AI after sending a message
                </h3>
              </div>
              <p className="mt-6 max-w-[53rem] type-body-sm text-ink-muted">
                Each clinician reply restarts this cooldown. AI summaries,
                drafts, prioritization, and safety triage continue running; only
                automatic patient-facing replies are paused.
              </p>
            </div>
            <SettingToggle
              on={enabled}
              label="Automatically silence AI after a clinician message"
              onChange={(next) => {
                setEnabled(next);
                setDirty(true);
                setNotice(null);
              }}
            />
          </div>

          <div className="flex flex-wrap items-end gap-8 border-t border-line-subtle pt-12">
            <label className="flex flex-col gap-4 type-label-xs text-ink-muted">
              PAUSE FOR
              <input
                type="number"
                min={1}
                max={unit === "days" ? 30 : unit === "hours" ? 720 : 43200}
                value={amount}
                onChange={(event) => {
                  const nextAmount = event.target.valueAsNumber;
                  setAmount(Number.isFinite(nextAmount) ? nextAmount : 0);
                  setDirty(true);
                  setNotice(null);
                }}
                className={`${fieldClass} w-[110px] min-w-[110px] tabular-nums`}
                aria-label="AI silence duration"
              />
            </label>
            <label className="flex flex-col gap-4 type-label-xs text-ink-muted">
              PERIOD
              <select
                value={unit}
                onChange={(event) => {
                  setUnit(event.target.value as SilenceDurationUnit);
                  setDirty(true);
                  setNotice(null);
                }}
                className={fieldClass}
                aria-label="AI silence duration unit"
              >
                <option value="minutes">minutes</option>
                <option value="hours">hours</option>
                <option value="days">days</option>
              </select>
            </label>
            <Button
              type="button"
              size="sm"
              disabled={!dirty || !valid}
              loading={update.isPending}
              onClick={() => void save()}
            >
              <Save size={13} aria-hidden /> Save rule
            </Button>
          </div>
          {!valid && (
            <p className="text-11 text-danger">
              Enter a duration between 1 minute and 30 days.
            </p>
          )}
          {notice && (
            <p
              role="status"
              className={`text-11 ${notice.includes("could not") ? "text-danger" : "text-success"}`}
            >
              {notice}
            </p>
          )}
        </Card>
      )}
    </section>
  );
}

function InboxTags() {
  const rules = useClinicianRulesAutomations();
  const session = useClinicianSession();
  const create = useCreateClinicianInboxTag();
  const [adding, setAdding] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const canEdit =
    session.data?.role === "admin" ||
    session.data?.role === "clinical_supervisor";
  const tags = useMemo(
    () =>
      [...(rules.data?.tags ?? [])].sort(
        (left, right) =>
          Number(right.kind === "safety") - Number(left.kind === "safety") ||
          right.priority - left.priority ||
          left.label.localeCompare(right.label),
      ),
    [rules.data?.tags],
  );

  const add = async (input: ClinicianInboxTagInput) => {
    setError(null);
    try {
      await create.mutateAsync(input);
      setAdding(false);
    } catch {
      setError("The tag could not be created. Nothing was changed.");
    }
  };

  return (
    <section className="flex flex-col gap-12">
      <div className="flex flex-wrap items-end justify-between gap-12">
        <div>
          <h2 className="type-heading-md">Inbox tags</h2>
          <p className="mt-4 max-w-[66rem] type-body-sm text-ink-muted">
            “When to apply” is matching criteria sent to AI triage for future
            patient messages; edits are not retroactive. Safety is a separate,
            protected clinical guardrail and cannot be weakened here.
          </p>
        </div>
        {canEdit && (
          <Button type="button" size="sm" onClick={() => setAdding(true)}>
            <Plus size={13} aria-hidden /> Add tag
          </Button>
        )}
      </div>

      {!canEdit && session.data && (
        <p className="border border-line bg-sunken px-12 py-9 type-body-sm text-ink-muted">
          Tag rules are shared across the clinical team. A clinical supervisor
          can edit them; they are read-only for your account.
        </p>
      )}

      {adding && (
        <TagEditor
          submitLabel="Create tag"
          loading={create.isPending}
          onCancel={() => {
            setAdding(false);
            setError(null);
          }}
          onSave={add}
        />
      )}
      {error && <p className="text-11 text-danger">{error}</p>}

      {rules.isLoading ? (
        <div className="h-[18rem] animate-pulse border border-line-subtle bg-sunken" />
      ) : rules.isError ? (
        <p className="border border-danger/30 bg-danger-wash p-14 type-body-sm text-danger">
          Inbox tags could not be loaded.
        </p>
      ) : (
        <div className="overflow-hidden border border-line-subtle bg-raised">
          <div className="hidden grid-cols-[minmax(12rem,0.7fr)_minmax(26rem,2fr)_10rem_10rem_8rem] gap-9 border-b border-line-subtle bg-sunken px-11 py-7 type-label-xs text-ink-muted md:grid">
            <span>TAG</span>
            <span>AI MATCHING CRITERIA</span>
            <span>PRIORITY</span>
            <span>STATUS</span>
            <span />
          </div>
          <div className="divide-y divide-line-subtle">
            {tags.map((tag) => (
              <TagRuleRow key={tag.id} tag={tag} canEdit={canEdit} />
            ))}
            {!tags.length && (
              <div className="flex flex-col items-center gap-7 p-24 text-center">
                <Tags size={20} className="text-ink-muted" aria-hidden />
                <p className="type-heading-sm">No inbox tags configured</p>
                <p className="type-body-sm text-ink-muted">
                  Add a topic rule to make recurring inbox work easier to scan.
                </p>
              </div>
            )}
          </div>
        </div>
      )}
    </section>
  );
}

function TagRuleRow({
  tag,
  canEdit,
}: {
  tag: ClinicianInboxTagRule;
  canEdit: boolean;
}) {
  const update = useUpdateClinicianInboxTag();
  const [editing, setEditing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const save = async (input: ClinicianInboxTagInput) => {
    setError(null);
    try {
      await update.mutateAsync({ id: tag.id, ...input });
      setEditing(false);
    } catch {
      setError("The tag could not be saved. Nothing was changed.");
    }
  };

  return (
    <article>
      <div className="grid gap-6 px-11 py-9 md:grid-cols-[minmax(12rem,0.7fr)_minmax(26rem,2fr)_10rem_10rem_8rem] md:items-center md:gap-9">
        <div className="flex min-w-0 items-center gap-6">
          <Badge
            tone={tag.kind === "safety" ? "danger" : priorityTone(tag.priority)}
            size="sm"
          >
            {tag.label}
          </Badge>
          {tag.locked && (
            <LockKeyhole
              size={12}
              className="text-ink-muted"
              aria-label="Protected tag"
            />
          )}
        </div>
        <p className="line-clamp-2 type-body-sm text-ink-secondary">
          {tag.rule}
        </p>
        <PriorityBadge priority={tag.priority} protectedTag={tag.locked} />
        <span className="type-label-xs text-ink-muted">
          {tag.enabled ? "ENABLED" : "OFF"}
        </span>
        <div className="flex justify-end">
          {canEdit && !tag.locked && (
            <button
              type="button"
              onClick={() => setEditing((current) => !current)}
              className="inline-flex items-center gap-4 type-label-xs hover:text-accent"
            >
              <Pencil size={12} aria-hidden /> {editing ? "Close" : "Edit"}
            </button>
          )}
        </div>
      </div>
      {editing && (
        <TagEditor
          tag={tag}
          submitLabel="Save tag"
          loading={update.isPending}
          error={error}
          onCancel={() => {
            setEditing(false);
            setError(null);
          }}
          onSave={save}
        />
      )}
    </article>
  );
}

function TagEditor({
  tag,
  submitLabel,
  loading,
  error,
  onCancel,
  onSave,
}: {
  tag?: ClinicianInboxTagRule;
  submitLabel: string;
  loading: boolean;
  error?: string | null;
  onCancel: () => void;
  onSave: (input: ClinicianInboxTagInput) => void | Promise<void>;
}) {
  const [label, setLabel] = useState(tag?.label ?? "");
  const [rule, setRule] = useState(tag?.rule ?? "");
  const [priority, setPriority] = useState<ClinicianInboxTagPriority>(
    tag?.priority ?? 1,
  );
  const [enabled, setEnabled] = useState(tag?.enabled ?? true);

  return (
    <div className="border-t border-line-subtle bg-sunken/50 px-11 py-10">
      <div className="grid gap-9 md:grid-cols-[minmax(14rem,0.7fr)_minmax(26rem,2fr)_12rem]">
        <label className="flex flex-col gap-4 type-label-xs text-ink-muted">
          TAG
          <input
            value={label}
            onChange={(event) => setLabel(event.target.value)}
            maxLength={60}
            placeholder="e.g. Shipping issue"
            className={fieldClass}
          />
        </label>
        <label className="flex flex-col gap-4 type-label-xs text-ink-muted">
          WHEN TO APPLY (AI MATCHING CRITERIA)
          <textarea
            value={rule}
            onChange={(event) => setRule(event.target.value)}
            maxLength={1000}
            rows={3}
            placeholder="Describe the patient messages that should receive this tag."
            className={`${fieldClass} py-8 resize-y`}
          />
        </label>
        <label className="flex flex-col gap-4 type-label-xs text-ink-muted">
          PRIORITY
          <select
            value={priority}
            onChange={(event) =>
              setPriority(
                Number(event.target.value) as ClinicianInboxTagPriority,
              )
            }
            className={fieldClass}
          >
            <option value={1}>Normal</option>
            <option value={2}>Elevated</option>
            <option value={3}>High</option>
          </select>
        </label>
      </div>
      <div className="mt-10 flex flex-wrap items-center justify-between gap-10">
        <SettingToggle
          on={enabled}
          label="Enable inbox tag"
          onChange={setEnabled}
        />
        <div className="flex items-center gap-6">
          <Button
            type="button"
            size="sm"
            variant="ghost"
            disabled={loading}
            onClick={onCancel}
          >
            Cancel
          </Button>
          <Button
            type="button"
            size="sm"
            loading={loading}
            disabled={!label.trim() || !rule.trim()}
            onClick={() =>
              void onSave({
                label: label.trim(),
                rule: rule.trim(),
                priority,
                enabled,
              })
            }
          >
            <Save size={12} aria-hidden /> {submitLabel}
          </Button>
        </div>
        {error && <p className="basis-full text-11 text-danger">{error}</p>}
      </div>
    </div>
  );
}

function InboxAutomations() {
  return <MessageAutomations />;
}

function PriorityBadge({
  priority,
  protectedTag,
}: {
  priority: ClinicianInboxTagPriority;
  protectedTag?: boolean;
}) {
  return (
    <Badge tone={protectedTag ? "danger" : priorityTone(priority)} size="sm">
      {protectedTag
        ? "CRITICAL"
        : priority === 3
          ? "HIGH"
          : priority === 2
            ? "ELEVATED"
            : "NORMAL"}
    </Badge>
  );
}

function priorityTone(priority: ClinicianInboxTagPriority) {
  return priority === 3 ? "warning" : priority === 2 ? "accent" : "neutral";
}

function SettingToggle({
  on,
  label,
  onChange,
}: {
  on: boolean;
  label: string;
  onChange: (next: boolean) => void;
}) {
  return (
    <div className="flex shrink-0 items-center gap-8">
      <span
        className={`type-label-sm ${on ? "font-medium text-accent" : "text-ink-muted"}`}
      >
        {on ? "On" : "Off"}
      </span>
      <button
        type="button"
        role="switch"
        aria-checked={on}
        aria-label={label}
        onClick={() => onChange(!on)}
        className={`relative block h-[24px] w-[44px] shrink-0 rounded-full border transition-colors ${
          on ? "border-accent bg-accent" : "border-line-strong bg-sunken"
        }`}
      >
        <span
          className={`absolute left-0 top-[1px] h-[20px] w-[20px] rounded-full border border-line bg-white shadow transition-transform ${
            on ? "translate-x-[21px]" : "translate-x-[2px]"
          }`}
        />
      </button>
    </div>
  );
}
